// Per-variety QC spec resolution — shared by the QC page (QC.tsx) and the
// printable per-run report (QCReport.tsx) so the band/flag logic never forks.
//
// A qc_template_items row carries ONE default expected_min/expected_max. The
// qc_item_variety_specs table (mig 698) optionally overrides those per variety
// (e.g. lemon vs nova box weight). Resolution is per-bound COALESCE — the SAME
// rule the qc_runs_compute_result SQL trigger uses, so the UI band, the report
// flag, the live indicator and the stored run.result all agree:
//
//   min = override.expected_min ?? item.expected_min
//   max = override.expected_max ?? item.expected_max
//
// Variety matching is the load-bearing part: qc_runs.variety is normalised by
// the DB's normalise_code() (upper + trim + collapse whitespace), and the spec
// table stores variety the same way (its BEFORE trigger). We MUST normalise
// both sides here too or a title-case 'Nova' override silently never matches a
// 'NOVA' run — the exact bug this feature fixes.

export interface QcItemVarietySpec {
  spec_id?:     string
  item_id:      string
  variety:      string
  expected_min: number | null
  expected_max: number | null
}

// Minimal shape the resolver needs from a template item — both QC.tsx's
// QcTemplateItem and QCReport.tsx's QcTemplateItemRow satisfy this.
interface SpecItem {
  item_id:      string
  expected_min: number | null
  expected_max: number | null
}

export interface ResolvedSpec {
  expected_min: number | null
  expected_max: number | null
  /** true when a per-variety override row supplied at least one bound. */
  perVariety:   boolean
}

// Best-effort JS mirror of the DB normalise_code(t)
// (NULLIF(regexp_replace(upper(trim(t)),'\s+',' ','g'),'')). The DB trigger is
// the CANONICAL normaliser — both qc_runs.variety and the spec table are stored
// through normalise_code, so the values we compare here are already normalised
// and we only need symmetric treatment of the two operands. (JS \s/trim cover
// Unicode whitespace that PG's ASCII \s/trim don't; harmless for variety codes,
// but don't treat this function's output as a canonical storage key.)
export function normaliseVariety(v: string | null | undefined): string {
  return (v ?? '').trim().toUpperCase().replace(/\s+/g, ' ')
}

// Resolve the effective spec for (item, variety). `variety` is typically the
// qc_run's variety (already uppercase). `specs` is any list of override rows —
// rows for other items/varieties are ignored, so callers can pass an unfiltered
// list. Falls back to the item's own bounds when no override matches.
export function resolveSpec(
  item: SpecItem,
  variety: string | null | undefined,
  specs: QcItemVarietySpec[] | null | undefined,
): ResolvedSpec {
  const want = normaliseVariety(variety)
  const override = want
    ? (specs ?? []).find(s => s.item_id === item.item_id && normaliseVariety(s.variety) === want)
    : undefined
  if (!override) {
    return { expected_min: item.expected_min, expected_max: item.expected_max, perVariety: false }
  }
  return {
    expected_min: override.expected_min ?? item.expected_min,
    expected_max: override.expected_max ?? item.expected_max,
    perVariety:   true,
  }
}

// A reading or bound may reach these comparisons as a STRING rather than a
// number even though the types say `number` — off a raw form field, a JSON
// payload, or a numeric column serialised as text (the codebase coerces
// defensively in the weather / fert / qty helpers for the same reason). A bare
// `<`/`>` on two strings is a lexicographic (char-by-char) comparison — `"9" >
// "12"` is TRUE because '9'(57) > '1'(49) — which would flip the pass/fail
// verdict for any reading whose leading digit or digit-count differs from a
// bound (brix, pressure, count, box weight …). Coerce every operand to a real
// number before comparing: a number round-trips unchanged, and a
// null/blank/non-finite value → null so it stays unbounded / not-scored, never
// silently 0.
function toNum(v: number | string | null | undefined): number | null {
  if (v == null) return null
  // Blank/whitespace-only → not scored. Without the trim, Number(' ') === 0
  // would score a blank reading as an in-spec 0.
  if (typeof v === 'string' && v.trim() === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

// Is a numeric answer outside the resolved band? Null bound = unbounded on that
// side; null value = not evaluated (false). Used by the report + live indicator.
export function isOutOfSpec(
  item: SpecItem,
  value: number | null,
  variety: string | null | undefined,
  specs: QcItemVarietySpec[] | null | undefined,
): boolean {
  const v = toNum(value)
  if (v == null) return false
  const { expected_min, expected_max } = resolveSpec(item, variety, specs)
  const lo = toNum(expected_min)
  const hi = toNum(expected_max)
  if (lo != null && v < lo) return true
  if (hi != null && v > hi) return true
  return false
}

// Per-item QC verdict for the response badge. 'info' = not yet evaluated (no
// response, or a blank/unanswered field). The pass/fail rules mirror the
// qc_runs compute_result SQL trigger (mig 176) so the UI badge agrees with the
// run.result the trigger stamps.
export type QcVerdict = 'pass' | 'fail' | 'info'

// Fields evaluateResponse needs from a template item: a SpecItem (for the
// numeric band) plus the response type and the YES/NO fail answer.
export interface VerdictItem extends SpecItem {
  response_type: string
  /** The YES_NO(_NA) answer that counts as a fail; defaults to 'NO'. */
  fail_answer:   string | null
}

// Fields it needs from a logged response.
export interface VerdictResponse {
  answer_number:  number | null
  answer_text:    string | null
  answer_options: string[] | null
}

// A NUMBER_RANGE answer is a fail when it falls outside either resolved bound
// (a null bound is unbounded on that side). Split out so evaluateResponse stays
// a flat per-type dispatch.
function numberRangeVerdict(value: number, band: ResolvedSpec): QcVerdict {
  // Coerce both operands — either may arrive as a string at runtime (see toNum).
  const v = toNum(value)
  if (v == null) return 'info' // blank/non-finite reading — not scored, like the DB null-answer path
  const lo = toNum(band.expected_min)
  const hi = toNum(band.expected_max)
  const tooLow  = lo != null && v < lo
  const tooHigh = hi != null && v > hi
  return (tooLow || tooHigh) ? 'fail' : 'pass'
}

export function evaluateResponse(
  item: VerdictItem,
  r: VerdictResponse | null | undefined,
  variety: string | null | undefined,
  specs: QcItemVarietySpec[] | null | undefined,
): QcVerdict {
  if (!r) return 'info'
  switch (item.response_type) {
    case 'NUMBER_RANGE':
      // Per-variety band (mig 698) resolved the same way as the DB trigger.
      return r.answer_number == null
        ? 'info'
        : numberRangeVerdict(r.answer_number, resolveSpec(item, variety, specs))
    case 'YES_NO':
    case 'YES_NO_NA':
      if (!r.answer_text) return 'info'
      return r.answer_text.toUpperCase() === (item.fail_answer ?? 'NO').toUpperCase() ? 'fail' : 'pass'
    case 'MULTI_SELECT':
      return (r.answer_options && r.answer_options.length > 0) ? 'fail' : 'pass'
    default:
      return 'info'
  }
}
